import React, { useState, useCallback, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
} from "@material-ui/core";

import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import useForm from "hooks/form";
import componentStyles from "assets/theme/views/admin/react-bs-table";
import Api from "lib/api/httpRequestHelper";
import PeriodField, {
  getPeriodLabel,
} from "components/_refactor/fields/period-field";
import TableRecebimentoODs from "./table/TableRecebimentoODs";
import ClearButton from "components/_refactor/button/clear";
import LoadingData from "components/Loading/LoadingData";
import moment from "moment";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import { IsMobile900, IsMobile } from "utils/is-mobile";
import InputText from "components/_refactor/text/input";

const useStyles = makeStyles(componentStyles);
const useCustomStyles = makeStyles({
  fields: {
    display: "flex",
    flexDirection: "column",
    width: 300,
    marginTop: "1rem",
  },
  option: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    wordBreak: "keep-all",
  },
  paper: {
    width: "max-content !important",
    minWidth: "100%",
  },
  popper: {
    maxWidth: "calc(100% - 60px - 1.5rem)",
    width: "max-content",
  },
});

const situacaoOptions = [
  { value: "sementradaloja", name: "OD Pendente na Loja" },
  { value: "comentradaloja", name: "OD Integrada na Loja" },
  { value: "integrada", name: "Sucesso Integração MIS" },
  { value: "naointegrada", name: "Erro de Integração MIS" },
  { value: "todos", name: "Todos" },
];

function RelatorioRecebimentoNFs() {
  const snackbar = useSnackbar();
  const classes = { ...useStyles(), ...useCustomStyles() };

  const [searching, setSearching] = useState(false);
  const [notasFiscais, setNotasFiscais] = useState(null);
  const [structureLoading, setStructureLoading] = React.useState([]);
  const [totalPaginas, setTotalPaginas] = useState(null);
  const [totalRegistros, setTotalRegistros] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(null);
  const [tableTitle, setTableTitle] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastSearchParams, setLastSearchParams] = useState(null);

  const isMobile900 = IsMobile900();
  const isMobile = IsMobile();

  const itemXS = isMobile ? 12 : isMobile900 ? 6 : 3;

  const { values, handleChange, errors } = useForm({
    numeroNf: "",
    periodoEmissao: null,
    dataInicialEmissao: null,
    dataFinalEmissao: null,
    situacao: "todos",
  });

  const searchTitles = useCallback(
    async (e, page = 1, pageSize = 25) => {
      setLastSearchParams(null);
      setSearching(true);
      setCancelLoading(false);
      setErrorMessage("");

      const initialFormat = moment(values.dataInicialEmissao).format(
        "YYYY-MM-DD"
      );
      const finalFormat = moment(values.dataFinalEmissao).format("YYYY-MM-DD");

      setLastSearchParams({
        dtIni: initialFormat,
        dtFim: finalFormat,
        numeroNf: values?.numeroNf,
        situacao: values?.situacao,
      });

      const searchParams =
        "?numeroNf=" +
        values.numeroNf +
        "&situacao=" +
        values.situacao +
        (values.periodoEmissao
          ? `&dtIni=${values.dataInicialEmissao}&dtFim=${values.dataFinalEmissao}`
          : "");

      await Api.get(
        "list-nf-ods",
        "invoice",
        searchParams + "&page=" + (page - 1) + "&pageSize=" + pageSize
      )
        .then((res) => {
          if (values.periodoEmissao) {
            setTableTitle(
              `Recebimento de ODs ${getPeriodLabel({
                period: values.periodoEmissao?.value,
                initialDate: values.dataInicialEmissao,
                finalDate: values.dataFinalEmissao,
              })}`
            );
          } else {
            setTableTitle(`Recebimento de ODs`);
          }

          setNotasFiscais(res.Result?.ListaNotasOds || []);
          setTotalPaginas(res.Result?.TotalDePaginas);
          setTotalRegistros(res.Result?.TotalRegistros);
        })
        .catch((error) => {
          console.log(error);
          setErrorMessage(error);        })
        .finally(() => {
          setCurrentPage(page);
          setCurrentSizePerPage(pageSize);
          setSearching(false);
        });
    },
    [
      values.dataFinalEmissao,
      values.dataInicialEmissao,
      values.numeroNf,
      values.periodoEmissao,
      values.situacao,
    ]
  );

  const handleClearFilters = () => {
    setSearching(true);
    setStructureLoading(true);
    handleChange({
      target: {
        name: "situacao",
        value: "todos",
      },
    });
    handleChange({
      target: {
        name: "numeroNf",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "periodoEmissao",
        value: null,
      },
    });
    handleChange({
      target: {
        name: "dataInicialEmissao",
        value: null,
      },
    });
    handleChange({
      target: {
        name: "dataFinalEmissao",
        value: null,
      },
    });
    setSearching(false);
    setStructureLoading(false);
  };

  const handleStopSearching = () => {
    setCancelLoading(true);
    setSearching(false);
    setTableTitle(null);
  };

  useEffect(() => {
    if (errorMessage !== "" && !cancelLoading) {
      snackbar.showMessage(errorMessage, {
        severity: "error",
      });
    }
  }, [cancelLoading, errorMessage]);

  return (
    <>
      <HeaderClientes
        section="Relatório de Recebimento de ODs"
        subsection="Estoque"
      />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <CardHeader
                className={classes.cardHeader}
                title="Filtros"
                titleTypographyProps={{
                  component: Box,
                  marginBottom: "0!important",
                  variant: "h3",
                }}
              ></CardHeader>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={itemXS}>
                    <AutoCompleteField
                      label="Situação"
                      required
                      options={situacaoOptions}
                      optionLabelKey="name"
                      optionIdKey="value"
                      value={
                        situacaoOptions.find(
                          (item) => item.value === values.situacao
                        ) || null
                      }
                      disabled={searching}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: "situacao",
                            value: newValue ? newValue.value : "",
                          },
                        });
                      }}
                    />
                  </Grid>

                  <Grid item xs={itemXS}>
                    <InputText
                      label="Número da NF"
                      type="number"
                      value={values.numeroNf}
                      onChange={(e) => {
                        handleChange({
                          target: {
                            name: "numeroNf",
                            value: e.target.value.slice(0, 50),
                          },
                        });
                      }}
                      disable={searching}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={
                      values.periodoEmissao?.value === "Outros" ? "12" : itemXS
                    }
                  >
                    <PeriodField
                      label="Data de Emissão"
                      value={values.periodoEmissao}
                      onChange={(option) =>
                        handleChange({
                          target: {
                            name: "periodoEmissao",
                            value: option,
                          },
                        })
                      }
                      initialDate={values.dataInicialEmissao}
                      finalDate={values.dataFinalEmissao}
                      setInitialDate={(value) =>
                        handleChange({
                          target: {
                            name: "dataInicialEmissao",
                            value,
                          },
                        })
                      }
                      setFinalDate={(value) =>
                        handleChange({
                          target: {
                            name: "dataFinalEmissao",
                            value,
                          },
                        })
                      }
                      disabled={searching}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={isMobile ? 12 : isMobile900 && 6}>
                    <Button
                      style={
                        isMobile || isMobile900
                          ? {
                              width: "100%",
                              marginBottom: "0.75rem",
                              marginTop: "0.75rem",
                            }
                          : { marginBottom: "0rem" }
                      }
                      variant="contained"
                      onClick={() => searchTitles()}
                      disabled={
                        searching ||
                        Object.keys(errors).length > 0 ||
                        structureLoading.length > 0
                      }
                    >
                      {searching ? "Pesquisando..." : "Pesquisar"}
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={isMobile ? 12 : isMobile900 && 6}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <ClearButton
                      disable={searching || structureLoading.length > 0}
                      onClick={() => handleClearFilters()}
                      fullWidth={true}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <LoadingData
              searching={searching}
              onClick={() => handleStopSearching()}
              classes={classes.root}
              label="Buscando dados..."
            />
          </Grid>
        </Grid>
        {!searching && notasFiscais && !cancelLoading ? (
          <TableRecebimentoODs
            isMobile={isMobile}
            dataTable={notasFiscais}
            totalPaginas={totalPaginas}
            totalRegistros={totalRegistros}
            searchTitles={searchTitles}
            currentPage={currentPage}
            currentSizePerPage={currentSizePerPage}
            tableTitle={tableTitle}
            lastSearchParams={lastSearchParams}
          />
        ) : null}
      </Container>
    </>
  );
}

export default RelatorioRecebimentoNFs;
